/**
 * CheckoutActionButton (Web)
 *
 * Renders the Apple Pay or Google Pay button when the matching wallet is
 * selected, otherwise renders the standard Button.
 *
 * Note: wallet availability checks are handled by PaymentMethodSelector.web.tsx.
 * This component assumes if a wallet is selected, it's already verified as available.
 */
import React from 'react';
import {StyleSheet, View} from 'react-native';

import {ApplePayButton} from '../../../components/payments/till/PaymentMethodSelector/applePay';
import {
  GooglePayButton,
  type GooglePayButtonWebProps,
  useGooglePayAvailability,
} from '../../../components/payments/till/PaymentMethodSelector/googlePay';
import Button from '../../../components/themed/Button';

import type {CheckoutActionButtonProps} from './CheckoutActionButton';

export type {CheckoutActionButtonProps};

// Type assertion: Webpack resolves GooglePayButton to .web.tsx on web, which
// accepts GooglePayButtonWebProps. TypeScript sees the base stub (.tsx, returns
// null, no props), so we cast via unknown.
const GooglePayButtonWeb =
  GooglePayButton as unknown as React.ComponentType<GooglePayButtonWebProps>;

/**
 * Checkout action button - web implementation.
 * Shows Apple Pay button when Apple Pay is selected (availability already verified by selector).
 */
export function CheckoutActionButton({
  selectedPaymentMethod,
  applePayConfig,
  googlePayWebConfig,
  onPayNow,
  isLoading = false,
  isDisabled = false,
  title = 'PAY NOW',
  testID,
  containerStyle,
  buttonStyle,
}: CheckoutActionButtonProps) {
  // Show Apple Pay button when Apple Pay is selected and we have config
  // Availability is already checked by PaymentMethodSelector
  const showApplePayButton = selectedPaymentMethod === 'applePay' && applePayConfig;

  // Show Google Pay button when Google Pay is selected and we have a web config.
  // remainingBalanceCents === 0 (gift cards fully cover the order): fall through
  // to the standard PAY NOW button — checkout submits via gift-card tender with
  // no payment instrument, and the payments lib rejects zero-total requests.
  const showGooglePayButton =
    selectedPaymentMethod === 'googlePay' &&
    Boolean(googlePayWebConfig) &&
    googlePayWebConfig?.remainingBalanceCents !== 0;

  // L2-01: GooglePayButton.web no longer runs its own probe — this component owns
  // the CTA decision and falls back to the card CTA when Google Pay is
  // unavailable. (PaymentMethodSelector.web still probes independently for the
  // tile, so this is not a single app-wide probe.) The probe is skipped unless
  // Google Pay is the active wallet branch (no pay.js load on the card / Apple Pay
  // paths). Called unconditionally, before any early return, to satisfy the rules
  // of hooks.
  const {isReady, isSupported, client, error} = useGooglePayAvailability({
    skip: !showGooglePayButton,
  });

  if (showApplePayButton) {
    return (
      <View style={[styles.container, containerStyle]} testID={testID}>
        <ApplePayButton
          serviceId={applePayConfig.serviceId}
          remainingBalanceCents={applePayConfig.remainingBalanceCents}
          onPaymentAuthorized={applePayConfig.onPaymentAuthorized}
          onCancel={applePayConfig.onCancel}
          onError={applePayConfig.onError}
          buttonStyle="black"
          buttonType="pay"
          disabled={isDisabled || isLoading}
        />
      </View>
    );
  }

  // The probe is "settled" once it reports support or surfaces an error. A
  // settled-not-ready result (or an error) means this client cannot pay: fall
  // through to the PAY NOW card button instead of rendering a blank area or an
  // indefinite spinner. While the probe is still in flight we keep the wallet
  // button mounted so it can render its own spinner and transition to the Google
  // Pay button once the probe resolves ready (no card→GP flicker).
  const googlePaySettled = isSupported || Boolean(error);
  const googlePayUnavailable = googlePaySettled && !isReady;

  if (showGooglePayButton && googlePayWebConfig && !googlePayUnavailable) {
    return (
      <View style={[styles.container, containerStyle]} testID={testID}>
        <GooglePayButtonWeb
          serviceId={googlePayWebConfig.serviceId}
          remainingBalanceCents={googlePayWebConfig.remainingBalanceCents}
          onPaymentAuthorized={googlePayWebConfig.onPaymentAuthorized}
          onCancel={googlePayWebConfig.onCancel}
          onError={googlePayWebConfig.onError}
          isReady={isReady}
          isSupported={isSupported}
          client={client}
          error={error}
          disabled={isDisabled || isLoading}
        />
      </View>
    );
  }

  // Default: standard card button. Mirror the wallet branches' explicit
  // full-width wrapper so card, Apple Pay and Google Pay render an identical
  // full-width, 52pt-tall CTA. In the desktop unified-checkout sidebar the
  // themed Button does not stretch on its own, so without forcing width at the
  // wrapper, the RNE container and the inner button it renders as a small button.
  return (
    <View style={[styles.container, containerStyle]}>
      <Button
        title={title}
        onPress={onPayNow}
        disabled={isDisabled}
        loading={isLoading}
        testID={testID}
        containerStyle={styles.fullWidth}
        buttonStyle={StyleSheet.flatten([styles.fullWidthButton, buttonStyle])}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    minHeight: 52,
  },
  fullWidth: {
    width: '100%',
  },
  fullWidthButton: {
    width: '100%',
    minHeight: 52,
    alignSelf: 'stretch',
  },
});

export default CheckoutActionButton;
